import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type BoardShareRecord from 'webapp/models/board-share';

export default class BoardShareSerializer extends ApplicationSerializer {
	override getReadOnlyFields(
		_json: Record<string, unknown>,
	): RecordData.BoardShare.ReadOnlyKey[] {
		return ['createdById'];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.BoardShare.Raw | RecordData.BoardShare.Serialized,
		prop: string | undefined,
	) {
		const { schedule, ...data } = json;
		// scheduleUser can be null before the object transform turns it into {}
		const attributes: Partial<Omit<RecordAttributes<BoardShareRecord>, 'scheduleUser'>> & {
			id?: string | number;
			scheduleUser?: BoardShareRecord['scheduleUser'] | null;
		} = omit(data as Omit<RecordData.BoardShare.Raw, 'schedule'>, 'createdBy');

		if ('createdBy' in data && data.createdBy) {
			attributes.createdById = data.createdBy.id;
		}

		if (schedule) {
			attributes.scheduleType = schedule.type;
			attributes.scheduleHourOfDay = schedule.hourOfDay;
			attributes.scheduleDayOfWeek = schedule.dayOfWeek || [];
			attributes.scheduleDayOfMonth = schedule.dayOfMonth;
			attributes.scheduleUser = 'user' in schedule ? schedule.user : undefined;
		} else {
			attributes.scheduleType = 0;
			attributes.scheduleHourOfDay = null;
			attributes.scheduleDayOfWeek = [];
			attributes.scheduleDayOfMonth = null;
			attributes.scheduleUser = null;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.BoardShare.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<BoardShareRecord>,
			RecordData.BoardShare.ReadOnlyKey
		>;

		const json: RecordData.BoardShare.Serialized = omit(
			attributes,
			'scheduleType',
			'scheduleHourOfDay',
			'scheduleDayOfWeek',
			'scheduleDayOfMonth',
			'scheduleUser',
		);

		if (attributes.scheduleType !== 0) {
			json.schedule = {
				type: attributes.scheduleType,
				hourOfDay: attributes.scheduleHourOfDay,
				dayOfWeek: attributes.scheduleDayOfWeek,
				dayOfMonth: attributes.scheduleDayOfMonth,
			};
		}

		return json;
	}
}
